import { z } from 'zod';

// ── Shared primitives ──────────────────────────────────────────────────────
export const postSlugSchema = z
  .string()
  .trim()
  .min(1, 'Slug là bắt buộc')
  .max(120, 'Slug tối đa 120 ký tự')
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Slug chỉ gồm chữ thường, số và dấu gạch ngang');

export const postTagsSchema = z
  .array(z.string().trim().min(1, 'Thẻ không được để trống').max(40, 'Thẻ tối đa 40 ký tự'))
  .max(8, 'Tối đa 8 thẻ');

export const postStatusSchema = z.enum(['draft', 'published']);

// ── Posts ───────────────────────────────────────────────────────────────────
export const adminCreatePostSchema = z.object({
  slug: postSlugSchema,
  title: z.string().trim().min(1, 'Tiêu đề là bắt buộc').max(200, 'Tiêu đề tối đa 200 ký tự'),
  excerpt: z.string().trim().min(1, 'Mô tả ngắn là bắt buộc').max(500, 'Mô tả ngắn tối đa 500 ký tự'),
  bodyMd: z.string().min(1, 'Nội dung là bắt buộc'),
  coverUrl: z.string().url('Ảnh bìa phải là một URL hợp lệ').max(2000).nullable().optional(),
  tags: postTagsSchema.default([]),
  status: postStatusSchema.default('draft'),
  publishedAt: z.coerce.date().nullable().optional(),
  authorId: z.string().min(1, 'Tác giả là bắt buộc'),
});

// Written out longhand, NOT derived from adminCreatePostSchema.partial() — see
// INVARIANTS §4.1 for why .partial() is unsafe here. Every field below is
// optional with no default at all.
export const adminUpdatePostSchema = z.object({
  slug: postSlugSchema.optional(),
  title: z.string().trim().min(1, 'Tiêu đề là bắt buộc').max(200, 'Tiêu đề tối đa 200 ký tự').optional(),
  excerpt: z
    .string()
    .trim()
    .min(1, 'Mô tả ngắn là bắt buộc')
    .max(500, 'Mô tả ngắn tối đa 500 ký tự')
    .optional(),
  bodyMd: z.string().min(1, 'Nội dung là bắt buộc').optional(),
  coverUrl: z.string().url('Ảnh bìa phải là một URL hợp lệ').max(2000).nullable().optional(),
  tags: postTagsSchema.optional(),
  status: postStatusSchema.optional(),
  publishedAt: z.coerce.date().nullable().optional(),
  authorId: z.string().min(1, 'Tác giả là bắt buộc').optional(),
});

export type AdminCreatePostInput = z.infer<typeof adminCreatePostSchema>;
export type AdminUpdatePostInput = z.infer<typeof adminUpdatePostSchema>;

// ── Authors ─────────────────────────────────────────────────────────────────
export const adminAuthorSchema = z.object({
  slug: postSlugSchema,
  name: z.string().trim().min(1, 'Tên tác giả là bắt buộc').max(120, 'Tên tác giả tối đa 120 ký tự'),
  role: z.string().trim().max(120, 'Vai trò tối đa 120 ký tự').nullable().optional(),
  bio: z.string().trim().max(2000, 'Tiểu sử tối đa 2000 ký tự').nullable().optional(),
  avatarUrl: z.string().url('Ảnh đại diện phải là một URL hợp lệ').max(2000).nullable().optional(),
  // Admin-only briefing note used to prompt an external model when drafting in
  // this author's voice. Never rendered publicly.
  //
  // 8000 rather than the 1200 this started at: a persona is a briefing
  // document, not a tagline — voice rules, biography, what the author will and
  // will not claim, recurring themes. The first real one written for this blog
  // came to 4100 characters and could not be saved. The cap exists only to
  // keep an unbounded blob out of a prompt, so it should sit well above what a
  // thorough brief needs, not below it.
  persona: z.string().max(8000, 'Persona tối đa 8000 ký tự').nullable().optional(),
});

export type AdminAuthorInput = z.infer<typeof adminAuthorSchema>;
